import { useSearchParams } from "react-router";
import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import { Calendar, FileText, Home, Store, Users, Wallet } from "lucide-react";
import { Badge } from "@/components/ui";
import {
  PageFrame,
  TransactionsSection,
  DetailTabPanel,
} from "@/components/common";
import { formatMoney } from "@/lib/money";
import { useImpersonation } from "@/hooks/useImpersonation";
import { useAuthStore } from "@/store/authStore";
import { profileService } from "@/services/profileService";
import { Permission } from "@/types";
import {
  AdminUserContactCard,
  type AdminUserContactLabels,
} from "./AdminUserContactCard";
import { AdminUserHeader } from "./AdminUserHeader";
import { AdminUserLegalInfoSection } from "./AdminUserLegalInfoSection";
import { AdminUserShortcuts, type AdminUserShortcut } from "./AdminUserShortcuts";
import {
  AdminUserDetailTabNav,
  type AdminUserDetailTabId,
} from "./AdminUserDetailTabNav";
import type {
  AdminUserContactActions,
  AdminUserDetail,
  AdminUserDetailLabels,
} from "./types";

interface TransactionsConfig {
  fetchTransactions: Parameters<
    typeof TransactionsSection
  >[0]["fetchTransactions"];
  fetchStats: Parameters<typeof TransactionsSection>[0]["fetchStats"];
  activities?: { id: string; name: string }[];
}

interface AdminUserDetailPageProps {
  user: AdminUserDetail | undefined;
  isLoading: boolean;
  hasError: boolean;
  listPath: string;
  labels: AdminUserDetailLabels;
  contactLabels: AdminUserContactLabels;
  bookings?: {
    path: string;
    title: string;
    seeBookings: string;
  };
  canToggleActive: boolean;
  isTogglingActive: boolean;
  onToggleActive: () => void;
  canEditDisplayName: boolean;
  isUpdatingDisplayName: boolean;
  onUpdateDisplayName: (name: string) => Promise<void>;
  contactActions?: AdminUserContactActions;
  relevantCountOverride?: number;
  structuresContent?: React.ReactNode;
  activitiesContent?: React.ReactNode;
  associationsContent?: React.ReactNode;
  transactions?: TransactionsConfig;
}

const TAB_QUERY_PARAM = "tab";

export function AdminUserDetailPage({
  user,
  isLoading,
  hasError,
  listPath,
  labels,
  contactLabels,
  bookings,
  canToggleActive,
  isTogglingActive,
  onToggleActive,
  canEditDisplayName,
  isUpdatingDisplayName,
  onUpdateDisplayName,
  contactActions,
  relevantCountOverride,
  structuresContent,
  activitiesContent,
  associationsContent,
  transactions,
}: AdminUserDetailPageProps) {
  const { t } = useTranslation("adminUsers");
  const { t: tSettings } = useTranslation("settings");
  const { startImpersonation, isStarting } = useImpersonation();
  const canImpersonate = useAuthStore((state) =>
    state.hasPermission(Permission.USERS_IMPERSONATE),
  );
  const [searchParams, setSearchParams] = useSearchParams();

  const availableTabs: AdminUserDetailTabId[] = [
    "overview",
    ...(structuresContent ? (["structures"] as const) : []),
    ...(activitiesContent ? (["activities"] as const) : []),
    ...(associationsContent ? (["associations"] as const) : []),
    "legal",
    ...(transactions ? (["transactions"] as const) : []),
  ];
  const requestedTab = searchParams.get(TAB_QUERY_PARAM);
  const activeTab: AdminUserDetailTabId =
    availableTabs.find((tab) => tab === requestedTab) ?? "overview";

  const setActiveTab = (tab: AdminUserDetailTabId) => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (tab === "overview") {
          next.delete(TAB_QUERY_PARAM);
        } else {
          next.set(TAB_QUERY_PARAM, tab);
        }
        return next;
      },
      { replace: true },
    );
  };

  // Same query as the Legal tab, so the overview can flag an incomplete profile.
  const userId = user?.userId;
  const { data: legalProfile } = useQuery({
    queryKey: ["admin", "user-legal-info", userId],
    queryFn: () => profileService.getProfile(userId!),
    enabled: !!userId,
  });

  if (isLoading) {
    return (
      <PageFrame
        backLink={{ fallback: listPath, children: labels.back }}
        isLoading
        loadingMessage={labels.loadDetail}
      />
    );
  }

  if (hasError || !user) {
    return (
      <PageFrame
        backLink={{ fallback: listPath, children: labels.back }}
        error
        errorMessage={labels.loadError}
        onRetry={() => window.location.reload()}
      />
    );
  }

  const relevantCount = relevantCountOverride ?? user.relevantCount;

  const shortcuts: AdminUserShortcut[] = [
    ...(bookings
      ? [
          {
            key: "bookings",
            icon: Calendar,
            tone: "bg-primary-50 text-primary-600 ring-primary-600/15",
            label: bookings.title,
            meta: bookings.seeBookings,
            to: bookings.path,
          },
        ]
      : []),
    ...(structuresContent
      ? [
          {
            key: "structures",
            icon: Home,
            tone: "bg-success-50 text-success-600 ring-success-600/15",
            label: t("detail.tabs.structures"),
            meta: t("detail.tabs.structuresDesc"),
            onSelect: () => setActiveTab("structures"),
          },
        ]
      : []),
    ...(activitiesContent
      ? [
          {
            key: "activities",
            icon: Store,
            tone: "bg-accent-50 text-accent-600 ring-accent-600/15",
            label: t("detail.tabs.activities"),
            meta: t("detail.tabs.activitiesDesc"),
            onSelect: () => setActiveTab("activities"),
          },
        ]
      : []),
    ...(associationsContent
      ? [
          {
            key: "associations",
            icon: Users,
            tone: "bg-violet-50 text-violet-600 ring-violet-600/15",
            label: t("detail.tabs.associations"),
            meta: t("detail.tabs.associationsDesc"),
            onSelect: () => setActiveTab("associations"),
          },
        ]
      : []),
    {
      key: "legal",
      icon: FileText,
      tone: "bg-sky-50 text-sky-600 ring-sky-600/15",
      label: t("detail.tabs.legal"),
      meta: legalProfile ? (
        <Badge
          variant={legalProfile.profile_completed ? "success" : "warning"}
          size="sm"
          dot
        >
          {legalProfile.profile_completed
            ? tSettings("forms.profileStatus.completeTitle")
            : tSettings("forms.profileStatus.incompleteTitle")}
        </Badge>
      ) : (
        t("detail.tabs.legalDesc")
      ),
      onSelect: () => setActiveTab("legal"),
    },
    ...(transactions
      ? [
          {
            key: "transactions",
            icon: Wallet,
            tone: "bg-indigo-50 text-indigo-600 ring-indigo-600/15",
            label: t("detail.tabs.transactions"),
            meta: `${labels.statWallet}: ${formatMoney(user.walletBalance)}`,
            onSelect: () => setActiveTab("transactions"),
          },
        ]
      : []),
  ];

  return (
    <PageFrame backLink={{ fallback: listPath, children: labels.back }}>
      <AdminUserHeader
        user={user}
        labels={labels}
        relevantCount={relevantCount}
        canEditDisplayName={canEditDisplayName}
        isUpdatingDisplayName={isUpdatingDisplayName}
        onUpdateDisplayName={onUpdateDisplayName}
        canImpersonate={canImpersonate}
        isImpersonating={isStarting}
        onImpersonate={() => {
          void startImpersonation(user.userId);
        }}
        canToggleActive={canToggleActive}
        isTogglingActive={isTogglingActive}
        onToggleActive={onToggleActive}
      />

      <div>
        <AdminUserDetailTabNav
          tabs={availableTabs}
          activeTab={activeTab}
          onTabChange={setActiveTab}
        />

        <DetailTabPanel
          id="admin-user-tabpanel-overview"
          labelledBy="admin-user-tab-overview"
          isActive={activeTab === "overview"}
        >
          <div className="grid items-start gap-5 xl:grid-cols-3">
            <AdminUserContactCard
              accountEmail={user.accountEmail}
              contactPhone={user.contactPhone}
              contactEmail={user.contactEmail}
              vatNumber={user.vatNumber}
              billingAddress={user.billingAddress}
              passwordResetEmail={user.passwordResetEmail}
              labels={contactLabels}
              actions={contactActions}
              className="mb-0 xl:col-span-2"
            />
            <AdminUserShortcuts
              title={t("detail.shortcutsTitle")}
              items={shortcuts}
              className="xl:sticky xl:top-20"
            />
          </div>
        </DetailTabPanel>

        {structuresContent ? (
          <DetailTabPanel
            id="admin-user-tabpanel-structures"
            labelledBy="admin-user-tab-structures"
            isActive={activeTab === "structures"}
          >
            {structuresContent}
          </DetailTabPanel>
        ) : null}

        {activitiesContent ? (
          <DetailTabPanel
            id="admin-user-tabpanel-activities"
            labelledBy="admin-user-tab-activities"
            isActive={activeTab === "activities"}
          >
            {activitiesContent}
          </DetailTabPanel>
        ) : null}

        {associationsContent ? (
          <DetailTabPanel
            id="admin-user-tabpanel-associations"
            labelledBy="admin-user-tab-associations"
            isActive={activeTab === "associations"}
          >
            {associationsContent}
          </DetailTabPanel>
        ) : null}

        <DetailTabPanel
          id="admin-user-tabpanel-legal"
          labelledBy="admin-user-tab-legal"
          isActive={activeTab === "legal"}
        >
          <AdminUserLegalInfoSection
            userId={user.userId}
            accountEmail={user.accountEmail}
            embedded
          />
        </DetailTabPanel>

        {transactions ? (
          <DetailTabPanel
            id="admin-user-tabpanel-transactions"
            labelledBy="admin-user-tab-transactions"
            isActive={activeTab === "transactions"}
          >
            <TransactionsSection
              entityId={user.userId}
              entityType={user.role}
              fetchTransactions={transactions.fetchTransactions}
              fetchStats={transactions.fetchStats}
              activities={transactions.activities}
              title={labels.transactionsTitle}
            />
          </DetailTabPanel>
        ) : null}
      </div>
    </PageFrame>
  );
}
